import React from 'react';

export type EmotionSlice = {
  emotion: string;
  percentage: number;
  color: string;
};

type EmotionPieChartProps = {
  emotions: EmotionSlice[];
  size?: number;
  isDark?: boolean;
};

const EMOTION_COLOR_MAP: Record<string, string> = {
  angry: '#f87171',
  rage: '#dc2626',
  irritated: '#fb7185',
  annoyed: '#f97316',

  sad: '#c9a0dc',
  down: '#a78bfa',
  grief: '#7c3aed',
  disappointed: '#94a3b8',
  heartbroken: '#8b5cf6',

  stressed: '#2dd4bf',
  pressure: '#14b8a6',
  anxious: '#4ade80',
  nervous: '#22c55e',
  worried: '#84cc16',
  panic: '#65a30d',
  overwhelmed: '#0f766e',

  guilty: '#a78bfa',
  ashamed: '#8b5cf6',
  regretful: '#c4b5fd',

  lonely: '#f472b6',
  isolated: '#ec4899',
  rejected: '#db2777',

  confused: '#686462',
  uncertain: '#6b7280',
  conflicted: '#4b5563',

  hopeful: '#38bdf8',
  optimistic: '#0ea5e9',
  inspired: '#22d3ee',

  grateful: '#34d399',
  thankful: '#10b981',
  relieved: '#6ee7b7',
  peaceful: '#67e8f9',
  calm: '#7dd3fc',
  content: '#93c5fd',
  secure: '#60a5fa',

  happy: '#fda247',
  joyful: '#f59e0b',
  excited: '#fb7185',
  energetic: '#ef4444',
  playful: '#f43f5e',
  proud: '#fbbf24',

  tired: '#9ca3af',
  exhausted: '#6b7280',
  sleepy: '#94a3b8',
  drained: '#64748b',

  frustrated: '#e879f9',
  helpless: '#d946ef',
  stuck: '#c026d3',

  scared: '#fbbf24',
  fearful: '#f59e0b',
  vulnerable: '#fde68a',

  curious: '#22d3ee',
  focused: '#3b82f6',
  motivated: '#10b981',
};

const FALLBACK_COLORS = [
  '#9b59b6', '#c9a0dc', '#2dd4bf', '#4ade80', '#a78bfa',
];

export function getEmotionColor(emotion: string, index: number): string {
  const key = emotion.toLowerCase();
  return EMOTION_COLOR_MAP[key] || FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(angleRad),
    y: cy + r * Math.sin(angleRad),
  };
}

function buildArcPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

function splitEmotionLabel(emotion: string): string[] {
  return [(emotion || '').trim()];
}

// Push label angles away from the horizontal extremes (90° = right, 270° = left)
// so labels land in upper/lower quadrants where there's more horizontal space.
function avoidHorizontalExtreme(angle: number): number {
  const a = ((angle % 360) + 360) % 360;
  const GAP = 42; // degrees to keep clear on each side of 90° and 270°
  if (a > 90 - GAP && a < 90 + GAP) {
    return a < 90 ? 90 - GAP : 90 + GAP;
  }
  if (a > 270 - GAP && a < 270 + GAP) {
    return a < 270 ? 270 - GAP : 270 + GAP;
  }
  return a;
}

const EmotionPieChart: React.FC<EmotionPieChartProps> = ({
  emotions,
  size = 180,
  isDark = true,
}) => {
  if (emotions.length === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 4;

  // Build slices with angle data
  let currentAngle = 0;
  const slices = emotions.map((item) => {
    const sliceAngle = (item.percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + sliceAngle;
    const midAngle = startAngle + sliceAngle / 2;
    currentAngle = endAngle;
    return { ...item, startAngle, endAngle, midAngle, sliceAngle };
  });

  // Radii for positioning
  const percentRadius = radius * 0.6;
  const dotRadius = radius + 6;
  const labelRadius = radius + 34;
  const margin = 100;
  const svgSize = size + margin * 2;

  return (
    <div className="flex items-center justify-center">
      <svg
        width={svgSize}
        height={svgSize}
        viewBox={`${-margin} ${-margin} ${svgSize} ${svgSize}`}
      >
        {/* Pie slices */}
        {slices.map((slice, i) => {
          const path =
            slice.percentage >= 100
              ? `M ${cx} ${cy} m -${radius} 0 a ${radius} ${radius} 0 1 0 ${radius * 2} 0 a ${radius} ${radius} 0 1 0 -${radius * 2} 0`
              : buildArcPath(cx, cy, radius, slice.startAngle, slice.endAngle);

          return (
            <path
              key={i}
              d={path}
              fill={slice.color}
              stroke={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'}
              strokeWidth={1.5}
            />
          );
        })}

        {/* Percentage labels inside slices */}
        {slices.map((slice, i) => {
          if (slice.sliceAngle < 18) return null;
          const pos = polarToCartesian(cx, cy, percentRadius, slice.midAngle);
          return (
            <text
              key={`pct-${i}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={slice.sliceAngle < 36 ? 10 : 12}
              fontWeight={600}
              fill="rgba(255,255,255,0.9)"
            >
              {slice.percentage}%
            </text>
          );
        })}

        {/* Connector dots on pie edge + emotion labels outside */}
        {slices.map((slice, i) => {
          const dotPos = polarToCartesian(cx, cy, dotRadius, slice.midAngle);
          // Adjust label angle away from horizontal extremes so labels land in
          // upper/lower quadrants where there is more horizontal space on screen.
          const adjustedAngle = avoidHorizontalExtreme(slice.midAngle);
          const labelPos = polarToCartesian(cx, cy, labelRadius, adjustedAngle);
          const isLeft = labelPos.x < cx;
          const lines = splitEmotionLabel(slice.emotion);
          const anchorX = isLeft ? labelPos.x - 6 : labelPos.x + 6;

          // Start connector from the text side that faces the pie, with a small
          // horizontal gap, instead of from the middle of the word.
          const CONNECTOR_GAP = 5;
          const lineStartX = isLeft ? anchorX + CONNECTOR_GAP : anchorX - CONNECTOR_GAP;
          const lineStartY = labelPos.y;

          const lineColor = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';

          return (
            <g key={`label-${i}`}>
              {/* White connector dot at pie edge */}
              <circle
                cx={dotPos.x}
                cy={dotPos.y}
                r={2.5}
                fill="white"
              />
              {/* Single diagonal: word midpoint → pie dot */}
              <line
                x1={lineStartX}
                y1={lineStartY}
                x2={dotPos.x}
                y2={dotPos.y}
                stroke={lineColor}
                strokeWidth={1}
              />
              {/* Emotion label */}
              <text
                x={anchorX}
                y={labelPos.y}
                textAnchor={isLeft ? 'end' : 'start'}
                dominantBaseline="middle"
                fontSize={11}
                fontWeight={500}
                fill={isDark ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)'}
              >
                {lines[0]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default EmotionPieChart;
